// src/components/PerfBenchmark.jsx
// 일별 포트 수익률(push_momo 누적) vs 코스피(KODEX200 069500). 누적·초과·승률.
import React, { useEffect, useMemo, useState } from "react";
import { fetchPerfDaily } from "../hooks/useSheets";

const BENCH = "069500";   // KODEX200 (^KS11은 야후 차트경로로 안 잡혀 ETF 대체)
const pct = (n, d = 2) => (n == null || isNaN(n)) ? "—" : `${n >= 0 ? "+" : ""}${(n * 100).toFixed(d)}%`;
const dstr = (ts) => new Date(ts * 1000).toISOString().slice(0, 10);

export default function PerfBenchmark() {
  const [perf, setPerf] = useState(null);       // [{date, portRet}]
  const [benchRet, setBenchRet] = useState(null); // {date: dailyRet}
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    fetchPerfDaily()
      .then((j) => { if (alive) setPerf((j.perf || []).slice().sort((a, b) => (a.date < b.date ? -1 : 1))); })
      .catch(() => { if (alive) setStatus("error"); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!perf || !perf.length) return;
    let alive = true;
    fetch(`/api/history?symbols=${BENCH}:KR&range=1y&interval=1d`)
      .then((r) => r.json())
      .then((j) => {
        const pts = (j.series?.[BENCH]?.points || []).slice().sort((a, b) => a[0] - b[0]);
        const map = {};
        for (let i = 1; i < pts.length; i++) {
          const p0 = pts[i - 1][1], p1 = pts[i][1];
          if (p0 > 0 && p1 > 0) map[dstr(pts[i][0])] = p1 / p0 - 1;
        }
        if (alive) { setBenchRet(map); setStatus("done"); }
      })
      .catch(() => { if (alive) setStatus("error"); });
    return () => { alive = false; };
  }, [perf]);

  const m = useMemo(() => {
    if (!perf || !benchRet) return null;
    const rows = perf.map((p) => ({ date: p.date, port: p.portRet, kospi: benchRet[p.date] }))
      .filter((r) => r.port != null && r.kospi != null);
    if (!rows.length) return { empty: true };
    let cumP = 1, cumK = 1, wins = 0, exSum = 0;
    rows.forEach((r) => { cumP *= 1 + r.port; cumK *= 1 + r.kospi; if (r.port > r.kospi) wins++; exSum += r.port - r.kospi; });
    return {
      n: rows.length, rows,
      cumPort: cumP - 1, cumKospi: cumK - 1, cumExcess: (cumP - 1) - (cumK - 1),
      winRate: wins / rows.length, avgExcess: exSum / rows.length,
    };
  }, [perf, benchRet]);

  return (
    <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, padding: 20, marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 800, color: "#1a1a2e" }}>코스피 대비 성과 (일별 누적)</h3>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 14 }}>포트 일간수익률(보유종목 노출가중 등락률) vs KOSPI(KODEX200) · {m && m.n ? `${m.n}거래일` : "—"}</div>

      {status === "loading" && <Note>📊 성과 데이터 불러오는 중…</Note>}
      {status === "error" && <Note>⚠️ 조회 실패 — 잠시 후 새로고침</Note>}
      {status === "done" && (!m || m.empty) && <Note>아직 데이터 없음 — push_momo가 매일 당일 수익률을 쌓습니다(백필 가능).</Note>}

      {m && !m.empty && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12, marginBottom: 14 }}>
            <Stat label="누적 초과 (vs 코스피)" value={pct(m.cumExcess)} color={m.cumExcess >= 0 ? "#0a7c59" : "#c0392b"} big />
            <Stat label="누적 포트" value={pct(m.cumPort)} color={m.cumPort >= 0 ? "#0a7c59" : "#c0392b"} />
            <Stat label="누적 코스피" value={pct(m.cumKospi)} color="#555" />
            <Stat label="승률 (코스피 이긴 날)" value={`${(m.winRate * 100).toFixed(0)}%`} color="#1a1a2e" />
            <Stat label="일평균 초과" value={pct(m.avgExcess)} color={m.avgExcess >= 0 ? "#0a7c59" : "#c0392b"} />
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: 340, width: "100%" }}>
              <thead>
                <tr style={{ color: "#888", fontWeight: 700 }}>
                  <th style={th}>날짜</th><th style={th}>포트</th><th style={th}>코스피</th><th style={th}>초과</th>
                </tr>
              </thead>
              <tbody>
                {m.rows.slice().reverse().slice(0, 15).map((r) => {
                  const ex = r.port - r.kospi;
                  return (
                    <tr key={r.date}>
                      <td style={td}>{r.date}</td>
                      <td style={{ ...td, textAlign: "right", color: r.port >= 0 ? "#0a7c59" : "#c0392b" }}>{pct(r.port, 2)}</td>
                      <td style={{ ...td, textAlign: "right", color: r.kospi >= 0 ? "#0a7c59" : "#c0392b" }}>{pct(r.kospi, 2)}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, color: ex >= 0 ? "#0a7c59" : "#c0392b" }}>{pct(ex, 2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const th = { textAlign: "right", padding: "5px 10px", borderBottom: "1px solid #eee" };
const td = { padding: "5px 10px", borderBottom: "1px solid #f6f6f6" };
function Stat({ label, value, color, big }) {
  return (
    <div style={{ background: "#fafafa", border: "1px solid #eee", borderRadius: 10, padding: "10px 12px", borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</div>
      <div style={{ fontSize: big ? 22 : 18, fontWeight: 700, color, marginTop: 3 }}>{value}</div>
    </div>
  );
}
function Note({ children }) {
  return <div style={{ padding: "12px 14px", background: "#f7f7f9", border: "1px solid #e8e8e8", borderRadius: 10, fontSize: 12.5, color: "#666" }}>{children}</div>;
}
